import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileWarning } from "lucide-react";
import { Nav } from "./Nav";
import { Footer } from "./Footer";

const withPlaceholders = (text) =>
  text.split(/(\[[^\]]+\])/g).map((part, i) =>
    /^\[[^\]]+\]$/.test(part) ? (
      <mark key={i} className="rounded bg-gold/40 px-1 font-semibold text-navy">{part}</mark>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    )
  );

const parse = (md) => {
  const blocks = md.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
  return blocks.map((b) => {
    if (b.startsWith("# ")) return { type: "h1", text: b.slice(2) };
    if (b.startsWith("## ")) return { type: "h2", text: b.slice(3) };
    if (b.startsWith("DRAFT FOR LEGAL REVIEW")) return { type: "draft", text: b };
    return { type: "p", text: b };
  });
};

const Block = ({ b }) => {
  if (b.type === "h1") return <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-navy leading-[1.05]">{b.text}</h1>;
  if (b.type === "h2") return <h2 className="mt-12 font-heading text-xl sm:text-2xl font-extrabold text-navy">{b.text}</h2>;
  if (b.type === "draft")
    return (
      <p data-testid="legal-draft-notice" className="mt-8 flex gap-3 rounded-2xl border border-gold bg-gold/15 p-5 text-sm sm:text-base leading-relaxed text-navy">
        <FileWarning className="mt-0.5 h-5 w-5 shrink-0 text-navy" aria-hidden="true" />
        <span>{withPlaceholders(b.text)}</span>
      </p>
    );
  return <p className="mt-4 text-base leading-relaxed text-navy/80">{withPlaceholders(b.text)}</p>;
};

export default function LegalPage({ slug }) {
  const [blocks, setBlocks] = useState([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    window.scrollTo(0, 0);
    fetch(`/legal/${slug}.md`)
      .then((r) => (r.ok ? r.text() : Promise.reject()))
      .then((t) => { if (alive) setBlocks(parse(t)); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [slug]);

  useEffect(() => {
    const h1 = blocks.find((b) => b.type === "h1");
    if (h1) document.title = `${h1.text} — KeepThis!`;
  }, [blocks]);

  return (
    <>
      <Nav />
      <main id="main" data-testid={`legal-page-${slug}`} className="px-5 pb-24 pt-32 sm:px-8 lg:pt-40">
        <article className="mx-auto max-w-3xl">
          <Link to="/" data-testid="legal-back-home" className="nav-link mb-10 inline-flex items-center gap-2 font-heading font-bold text-navy">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to home
          </Link>
          {failed && <p className="text-navy/80">This policy couldn't be loaded. Please try again later.</p>}
          {blocks.map((b, i) => <Block key={i} b={b} />)}
        </article>
      </main>
      <Footer />
    </>
  );
}
